import { ArrowRight } from 'lucide-react';
import { buttonVariants } from './ui/Button';
import { LocalizedLink } from './LocalizedLink';
import { CloseTheLoopDiagram } from './CloseTheLoopDiagram';
import { useT } from '@/i18n/provider';
import { trackEvent } from '@/lib/analytics';

/**
 * "Close the loop": building is one half, shipping is the other. The coding
 * agent builds and ships, LogPulse watches production for performance and
 * attacks, and the same agent reads that over MCP and proposes the fix, which
 * a person approves. The full story, for ads, lives on /close-the-loop.
 */
const STEPS = ['build', 'ship', 'watch', 'fix'] as const;

export function CloseTheLoopSection() {
  const t = useT();

  return (
    <section className="py-20 sm:py-28" aria-labelledby="close-loop-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
        <div className="animate-on-scroll">
          <p className="eyebrow">{t('closeLoop.eyebrow')}</p>
          <h2
            id="close-loop-heading"
            className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance"
          >
            {t('closeLoop.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t('closeLoop.subtitle')}</p>
          <ol className="mt-8 space-y-4">
            {STEPS.map((key, i) => (
              <li key={key} className="flex gap-4">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-primary-500 text-sm font-bold text-primary-700 dark:text-primary-300">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-gray-950 dark:text-white">{t(`closeLoop.${key}.title`)}</p>
                  <p className="text-gray-600 dark:text-gray-400">{t(`closeLoop.${key}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">{t('closeLoop.free')}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="https://app.logpulse.io/sign-up"
              onClick={() => trackEvent('cta_click', { surface: 'close_loop' })}
              className={buttonVariants({ variant: 'default', size: 'lg' })}
            >
              {t('closeLoop.cta.primary')}
              <ArrowRight className="h-4 w-4 ml-1" />
            </a>
            <LocalizedLink
              to="/close-the-loop"
              className={buttonVariants({ variant: 'outline', size: 'lg' })}
            >
              {t('closeLoop.cta.secondary')}
            </LocalizedLink>
          </div>
        </div>

        <CloseTheLoopDiagram className="animate-on-scroll" />
      </div>
    </section>
  );
}
